import React, { ReactElement } from 'react';
import View from "../View/View";
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  FilterCombo,
  PuzzleTooltip as TpTooltip,
  WithTooltip,
  PuzzleButton,
  FORM_CONTROL,
  withFormControl,
  noDataDropDownRender,
  PuzzleToolbar, HBox,
} from "puzzle";
import { config, tptConfig } from "./Create.config";
import SubmissionAbstract from "../SubmissionAbstract";
import PoliciesManager from "../../util/Policies";
import { CREATE_STATE, DETAILS_FORM_DATA, PROJECT_MANAGER } from "../Types";
import _ from "lodash";
import classNames from "classnames";
import { orderBy } from '@progress/kendo-data-query';
import { Container } from "puzzle";
import { Field } from "../../UI/Field/Field";

export type PROPS = {
  form: FORM_CONTROL,
  submissionMode: string
  isTemplate?: boolean
};

class Create extends SubmissionAbstract<PROPS, CREATE_STATE> {
  tooltip: any;
  static defaultProps = {
    isTemplate: false,
    submissionMode: 'CREATE',
    dispatcherUpdateStates: {
      uploadFlowState: {}
    }
  };

  /**
   * Cancel button handler, prompts warning window and redirects to previous path
   */
  onCancel = () => {
    this.dispatch('checkForChangesBeforeRedirect', '', { leaveAction: 'goBack' })
  }

  /**
   * Get formData
   */
  getData = () => {
    let formData = this.props.form.getData();
    if (formData) {
      let { templateDescription, languageDirectionData } = this.state;
      let { isTemplate } = this.props;
      formData = Object.assign(formData, {
        languageDirections: languageDirectionData[0].target,
        description: templateDescription,
        isTemplate: isTemplate
      });
    }

    return formData;
  };

  /**
   * Set data for editing
   */
  setData = (data: DETAILS_FORM_DATA, afterTemplateChange: boolean = false) => {
    // @ts-ignore
    data.projectManager = data.projectManager.map((item: PROJECT_MANAGER) => item.userId);
    const { submissionMode } = this.props;

    this.setState({
      submissionMode: submissionMode,
      templateDescription: data.description
    });

    // setting data in form control must align with config provided otherwise bugs!
    delete data.completed;
    delete data.started;
    delete data.submissionMode;
    delete data.batchInfos;
    let submissionData = _.cloneDeep(data);
    let formData = this.props.form.getData();
    if (afterTemplateChange && formData.submissionName) {
      submissionData = _.omit(submissionData, 'submissionName')
    }
    this.props.form.setData(submissionData);
  };

  /**
   * Open add template description dialog
   */
  openTemplateDescription = () => {
    let { templateDescription } = this.state;
    this.dispatch('openTemplateDescription', '', { templateDescription });
  };

  /**
   * Set templates
   */
  setTemplatesData = (templatesData: any) => {
    const { items } = templatesData;
    const selectableTpls = items.filter((item: any) => item.userContextPolicies?.POLICY_PD_ADD_SUBMISSION_FROM_TEMPLATE)
    this.setState({
      templatesData: orderBy(selectableTpls, [{
        field: 'isFavorite',
        dir: 'desc'
      }, {
        field: 'submissionName',
        dir: 'asc'
      }])
    })
  };

  /**
   * Set template description
   */
  setTemplateDescription = (templateDescription: string) => {
    this.setState({
      templateDescription
    });
  };

  /**
   * Handle template change
   */
  handleTemplateChange = (event: ComboBoxChangeEvent) => {
    let target = event.target;
    const templateId = target.value;
    const { template: oldTemplateId } = this.props.form.getData();
    let { submissionMode } = this.props;

    this.dispatch('confirmTemplateChange', '', { selectedTemplate: templateId, oldTemplateId, submissionMode });
  };

  /**
   * Template dropDown list item render
   * @param li
   * @param itemProps
   */
  templateItemRender = (li: ReactElement<HTMLLIElement>, itemProps: any): React.ReactNode => {
    const { isFavorite } = itemProps.dataItem;
    const iconClass = classNames(
      'tp-icon tp-icon-sm',
      {
        'tp-star-outline': !isFavorite,
        'tp-favorite-color tp-star': isFavorite
      }
    );
    const props = {
      ...li.props,
      id: itemProps.id?.substr(7, itemProps.id?.length)
    };
    if (isFavorite) {
      Object.assign(props, { 'aria-label': `${this.translate('SRL-favoriteTemplateIcon')}` })
    }
    const itemChildren =
      <>
        <i className={iconClass} aria-hidden={true} />
        {li.props.children}
      </>;
    return React.cloneElement(li, props, itemChildren);
  };

  renderBody() {
    const {
      templatesData,
      uploadFlowState
    } = this.state;

    const {
      description,
      template,
      uploadedFiles,
      isMultiBatch,
      batchesWithFiles,
      project,
      submissionMode,
      isTemplate,
      specificDate,
      mslaLevel
    } = this.props.form.getData();

    const descriptionClass: string = description?.length ? 'active' : '';

    const {
      hasSubmissionSavePolicy,
      hasTemplatesFolderPolicy,
      hasApplyTemplatePolicy,
      getProjectPolicy,
      hasSubmissionCreateFromTemplatePolicy
    } = PoliciesManager;
    const uploadedFilesExist = isMultiBatch ? batchesWithFiles : !!uploadedFiles?.length;
    const hasSubmissionStartPolicy = getProjectPolicy(project, ['hasSubmissionStartPolicy', 'hasEditDueDatePolicy']);
    const hasSubmissionAnalyzePolicy = getProjectPolicy(project, ['hasSubmissionAnalyzePolicy', 'hasEditDueDatePolicy']);
    const hasSelectSuppliersButtonPolicies = getProjectPolicy(project, 'hasSelectVendorsButtonsDialogPolicy');

    const fileUploadInProgress = !this.isAllUploadEnd();
    const infoIconAriaLabel = this.translate('SRL-templateDescription', { desc: description });
    const mslaLevelCheck = mslaLevel ? !!specificDate : true;

    const templateClass = !isTemplate && submissionMode !== 'EDIT' && hasTemplatesFolderPolicy && hasSubmissionCreateFromTemplatePolicy ? 'tp-page-header-with-template' : '';

    return (
      <Container
        vBox
        className={'tp-page tp-submission-page'}
      >
        <Container
          displayFlex
          wrap
          htmlTag={'section'}
          gap={4}
          tabIndex={0}
          className={`tp-page-header ${templateClass}`}
        >
          <HBox alignItemsEnd flex1 className={'pd-page-header-left'}>
            <h2 className={'tp-page-title'}>
              {this.translate('newSubmission')}
            </h2>
            {
              !isTemplate && submissionMode !== 'EDIT' && hasTemplatesFolderPolicy && hasSubmissionCreateFromTemplatePolicy ?
                <Field
                  className='tp-template-field-wrap k-flex-1 k-justify-content-start'
                  withAdditionalEl
                >
                  <FilterCombo
                    id={'template'}
                    label={this.translate('templateLabel')}
                    disabled={!hasApplyTemplatePolicy}
                    data={templatesData}
                    listNoDataRender={() => noDataDropDownRender(this.translate('noData').toUpperCase())}
                    itemRender={this.templateItemRender}
                    name='template'
                    textField='submissionName'
                    valueField="submissionId"
                    onChange={this.handleTemplateChange}
                    popupSettings={{ className: 'tp-create-from-template-dropdown-list' }}
                    handleChangeByState={true}
                    translateMethod={this.translate.bind(this)}
                    valueAsSimpleType={true}
                    {...template}
                  />
                  <WithTooltip
                    context={this}
                    htmlTag='span'
                    title={description}
                  >
                    <i
                      tabIndex={description?.length ? 0 : undefined}
                      aria-label={description?.length ? infoIconAriaLabel : undefined}
                      aria-hidden={description?.length ? "false" : "true"}
                      className={`${descriptionClass} tp-icon tp-info tp-color-light-LM`}
                      title={description}
                    />
                  </WithTooltip>
                </Field>
                : null
            }
          </HBox>
          <PuzzleToolbar className="tp-page-header-action-toolbar k-flex-1">
            <PuzzleButton
              fillMode={'outline'}
              onClick={this.onCancel}
            >
              {this.translate('cancel')}
            </PuzzleButton>
            {uploadedFilesExist && hasSelectSuppliersButtonPolicies ?
              <PuzzleButton
                disabled={fileUploadInProgress}
                onClick={this.triggerSubmissionAction.bind(this, 'selectSuppliersSubmissionAction', { project })}
                themeColor={'primary'}
              >
                {this.translate('selectSuppliers')}
              </PuzzleButton> : null}
            {hasSubmissionSavePolicy ?
              <PuzzleButton
                disabled={fileUploadInProgress}
                onClick={this.triggerSubmissionAction.bind(this, 'saveSubmission', { staticFields: this.staticFields })}
                themeColor={'primary'}
              >
                {this.translate('save')}
              </PuzzleButton> : null}
            {uploadedFilesExist && hasSubmissionAnalyzePolicy ?
              <PuzzleButton
                disabled={fileUploadInProgress}
                onClick={this.triggerSubmissionAction.bind(this, 'analyzeSubmission', {})}
                themeColor={'primary'}
              >
                {this.translate('analyze')}
              </PuzzleButton> : null}
            {uploadedFilesExist && hasSubmissionStartPolicy && mslaLevelCheck ?
              <PuzzleButton
                disabled={fileUploadInProgress}
                onClick={this.triggerSubmissionAction.bind(this, 'startSubmissionDialog', { project })}
                themeColor={'primary'}
              >
                {this.translate('start')}
              </PuzzleButton> : null}
          </PuzzleToolbar>
        </Container>
        <View
          {...this.props}
          headerText={this.translate('newSubmission')}
          projectPolicies={PoliciesManager.inProject(project)}
          fileUploadInProgress={fileUploadInProgress}
          uploadFlowState={uploadFlowState}
        />
        <TpTooltip
          className={'tp-templateDescription-tooltip'}
          ref={(el: any) => this.tooltip = el}
          anchorElement="target"
          openDelay={1000}
          position="right"
        />
      </Container>
    )
  }
}

const Tpt = withFormControl(tptConfig, false)(Create);
const Client = withFormControl(config, false)(Create);

const CreateView = (props: { tptEnabled: boolean }): JSX.Element => {
  const mode = props.tptEnabled ? <Tpt tptEnabled={true} /> : <Client tptEnabled={false} />;
  return mode;
};

export default CreateView;
